import i18next from 'i18next';
import _ from 'lodash';
import { StellantisQuoteStatus } from 'src/api/models/Enums';
import {
	StellantisQuoteData,
	StellantisQuoteRequestDTO,
} from 'src/api/models/StellantisDTO';
import { differenceInMinutes, isAfter } from 'date-fns';

type OptionalQuoteRequest = StellantisQuoteRequestDTO | null | undefined;

export const isNilOrEmpty = (value: any) => {
	if (_.isNil(value)) return true;

	if (_.isString(value)) {
		return value?.trim() === '';
	}

	return false;
};

export const isPending = (quoteRequest: OptionalQuoteRequest) => {
	return quoteRequest?.status?.toUpperCase() === StellantisQuoteStatus.PENDING;
};

export const isInProgress = (quoteRequest: OptionalQuoteRequest) => {
	return (
		quoteRequest?.status?.toUpperCase() === StellantisQuoteStatus.IN_PROGRESS
	);
};

export const isQuoted = (quoteRequest: OptionalQuoteRequest) => {
	return quoteRequest?.status?.toUpperCase() === StellantisQuoteStatus.QUOTED;
};

export const isUnquotable = (quoteRequest: OptionalQuoteRequest) => {
	return (
		quoteRequest?.status?.toUpperCase() === StellantisQuoteStatus.UNQUOTABLE
	);
};

export const isAzienda = (quoteRequest: OptionalQuoteRequest) => {
	return quoteRequest?.quote?.tipoCliente?.toLowerCase() === 'azienda';
};

export const isPrivato = (quoteRequest: OptionalQuoteRequest) => {
	return quoteRequest?.quote?.tipoCliente?.toLowerCase() === 'privato';
};

export const isLiberoProfessionista = (quoteRequest: OptionalQuoteRequest) => {
	return (
		quoteRequest?.quote?.tipoCliente?.toLowerCase() === 'libero_professionista'
	);
};

export const calculateTimeLeft = (deadline: Date) => {
	const now = new Date();

	const totalMinutesLeft = differenceInMinutes(deadline, now);
	const hoursLeft = Math.floor(totalMinutesLeft / 60);
	const minutesLeft = totalMinutesLeft % 60;

	if (isAfter(now, deadline)) {
		return {
			hours: 0,
			minutes: 0,
			passed: {
				hours: Math.ceil(totalMinutesLeft / 60),
				minutes: minutesLeft,
			},
		};
	}

	return { hours: hoursLeft, minutes: minutesLeft };
};

export const assignCounterInfo = (item: StellantisQuoteRequestDTO) => {
	const deadline = new Date(item.responseRequiredDate + 'Z');
	const timeLeft = calculateTimeLeft(deadline);
	let status: 'ok' | 'warning' | 'expired' = 'ok';

	if (timeLeft.hours === 0 && timeLeft.minutes === 0) {
		status = 'expired';
	} else if (timeLeft.hours < 2) {
		status = 'warning';
	} else {
		status = 'ok';
	}

	item.countdownStatus = status;
	item.timeLeft = timeLeft;

	return item;
};

export const assignCounterInfoV2 = (item: StellantisQuoteRequestDTO) => {
	const remainingMinutes = item.remainingMinutes || 0;

	const timeLeft = {
		hours: remainingMinutes < 0 ? 0 : Math.floor(remainingMinutes / 60),
		minutes: remainingMinutes < 0 ? 0 : remainingMinutes % 60,
		passed: {
			hours: remainingMinutes < 0 ? Math.ceil(remainingMinutes / 60) : 0,
			minutes: remainingMinutes < 0 ? remainingMinutes % 60 : 0,
		},
	};

	let status: 'ok' | 'warning' | 'expired' = 'ok';

	if (timeLeft.hours === 0 && timeLeft.minutes === 0) {
		status = 'expired';
	} else if (timeLeft.hours < 2) {
		status = 'warning';
	} else {
		status = 'ok';
	}

	item.countdownStatus = status;
	item.timeLeft = timeLeft;

	return item;
};

const getProcessingTime = (item: StellantisQuoteRequestDTO) => {
	try {
		const requestDate = new Date(item.requestDate + 'Z');
		const quotedAt = new Date(item.quotedAt + 'Z');
		const updatedAt = new Date(item.updatedAt + 'Z');
		return differenceInMinutes(
			isUnquotable(item) ? updatedAt : quotedAt,
			requestDate,
			{ roundingMethod: 'ceil' }
		);
	} catch (error) {
		console.warn('Failed to compute processing time', { item, error });
	}
};

const getProcessingTimeV2 = (item: StellantisQuoteRequestDTO) => {
	try {
		if (item.responseMinutes === null || item.responseMinutes === undefined) {
			return getProcessingTime(item);
		}
		return item.responseMinutes;
	} catch (error) {
		console.warn('Failed to compute processing time', { item, error });
	}
};

export const mapQuoteRequests = (items: StellantisQuoteRequestDTO[]) => {
	return items.map((item, idx) => {
		if (!item.quote) {
			item.quote = { ...item.request };
		}

		if (isQuoted(item) || isUnquotable(item)) {
			item.processingTime = getProcessingTimeV2(item);
		}

		assignCounterInfoV2(item);
		return item;
	});
};

const DIFF_FIELDS = [
	'marca',
	'modello',
	'durata',
	'incendioFurtoScopertoPerc',
	'kaskoFranchigia',
	'kmAnnui',
	'rcaPenaleRisarcitoria',
	'anticipoNoIva',
	'gommeYN',
	'tipoGomma',
	'gommeIllimitateYN',
	'numTrenoGommeEstive',
	'numTrenoGommeInvernali',
	'autoSostitutivaYN',
	'autoSostitutivaGruppo',
	'tipoCliente',
	'nome',
	'cognome',
	'ragioneSociale',
	'requestNote',
	'optionals',
];

export const quoteDiff = (
	request: StellantisQuoteData,
	quote?: StellantisQuoteData,
	keys: string[] = DIFF_FIELDS
) => {
	if (!quote) return {};

	return keys.reduce((prev, key) => {
		const oldValue = request[key];
		const newValue = quote[key];

		if (key === 'optionals') {
			const oldDescriptions = oldValue?.map((item) => item?.descrizione) || [];
			const newDescriptions = newValue?.map((item) => item?.descrizione) || [];
			const newOptionals = _.difference(
				oldDescriptions,
				newDescriptions
			).concat(_.difference(newDescriptions, oldDescriptions));

			if (!newOptionals.length) return prev;

			prev[key] = {
				oldValue: oldDescriptions?.join('\n'),
				newValue: newDescriptions?.join('\n'),
			};
			return prev;
		}

		const isBooleanField = [
			'gommeYN',
			'gommeIllimitateYN',
			'autoSostitutivaYN',
		].includes(key);

		if (oldValue != newValue) {
			prev[key] = {
				oldValue: isBooleanField
					? oldValue
						? i18next.t('yes')
						: i18next.t('no')
					: oldValue,
				newValue: isBooleanField
					? newValue
						? i18next.t('yes')
						: i18next.t('no')
					: newValue,
			};
		}

		return prev;
	}, {});
};
